import { useEffect, useRef, useCallback } from 'react';
import { createAuditLog } from '@/lib/api';

// Generate session ID
const getSessionId = () => {
  let sessionId = sessionStorage.getItem('audit_session_id');
  if (!sessionId) {
    sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('audit_session_id', sessionId);
  }
  return sessionId;
};

// Track page visit duration
export const usePageTracking = (pageName, details = {}) => {
  const startTime = useRef(Date.now());
  const sessionId = getSessionId();

  useEffect(() => {
    // Log page visit
    createAuditLog({
      action: 'page_visit',
      details: { page: pageName, ...details },
      session_id: sessionId
    }).catch(() => {}); // Silent fail

    return () => {
      // Log time spent when leaving
      const duration = Math.floor((Date.now() - startTime.current) / 1000);
      if (duration > 2) { // Only log if spent more than 2 seconds
        createAuditLog({
          action: 'page_visit',
          details: { page: pageName, action: 'leave', ...details },
          session_id: sessionId,
          duration_seconds: duration
        }).catch(() => {});
      }
    };
  }, [pageName]);
};

// Track specific actions
export const useAuditLog = () => {
  const sessionId = getSessionId();

  const logAction = useCallback((action, details = {}) => {
    createAuditLog({
      action,
      details,
      session_id: sessionId
    }).catch(() => {}); // Silent fail
  }, [sessionId]);

  const logProblemClick = useCallback((problemId, problemTitle, vertical) => {
    logAction('problem_click', { 
      problem_id: problemId, 
      problem_title: problemTitle,
      vertical 
    });
  }, [logAction]);

  const logVerticalAccess = useCallback((verticalId, verticalName) => {
    logAction('vertical_access', { 
      vertical_id: verticalId, 
      vertical: verticalName 
    });
  }, [logAction]);

  const logAIQuestion = useCallback((question, aiType = 'consultant') => {
    logAction('ai_question', { 
      question: question.substring(0, 200), 
      ai_type: aiType 
    });
  }, [logAction]);

  return {
    logAction,
    logProblemClick,
    logVerticalAccess,
    logAIQuestion
  };
};

export default useAuditLog;
